import React, { useState, useCallback, useMemo, memo, useEffect } from 'react';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { MapContainer, TileLayer, useMap, ZoomControl } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import 'leaflet/dist/leaflet.css';
import { mapStateAtom, mapBoundsAtom, isMapLoadingAtom, hasUserInteractedWithMapAtom } from '../atoms/map';
import { incidentsAtom, selectedIncidentAtom } from '../atoms/incidents';
import { userLocationAtom } from '../atoms/location';
import { fetchIncidents } from '../services/incidents';
import { logger } from '../lib/logger';
import MapController from './map/MapController';
import MapUpdater from './map/MapUpdater';
import BoundsUpdater from './map/BoundsUpdater';
import MapDebugOverlay from './map/MapDebugOverlay';
import FetchDebugOverlay from './map/FetchDebugOverlay';
import IncidentMarker from './map/IncidentMarker';
import UserLocationMarker from './map/UserLocationMarker';
import RecenterButton from './map/RecenterButton';
import { locationPermissionAtom, locationErrorAtom } from '../atoms/location';
import { isInHaiti } from '../utils/locationUtils';

export default memo(function IncidentMap() {
  const [mapState, setMapState] = useAtom(mapStateAtom);
  const [mapBounds, setMapBounds] = useAtom(mapBoundsAtom);
  const setIsMapLoading = useSetAtom(isMapLoadingAtom);
  const [incidents] = useAtom(incidentsAtom);
  const [userLocation] = useAtom(userLocationAtom);
  const setSelectedIncident = useSetAtom(selectedIncidentAtom);
  const [map, setMap] = useState<L.Map | null>(null);
  const [hoveredIncidentId, setHoveredIncidentId] = useState<string | null>(null);
  
  // Use the atom properly with useAtomValue
  const hasUserInteractedWithMap = useAtomValue(hasUserInteractedWithMapAtom);

  const navigate = useNavigate();
  const [locationPermission] = useAtom(locationPermissionAtom);
  const [locationError] = useAtom(locationErrorAtom);

  // Add state to track if we've already handled a non-Haiti location
  const [hasHandledNonHaitiLocation, setHasHandledNonHaitiLocation] = useState(false);

  // Fetch incidents when bounds change
  const { data: fetchedIncidents, isLoading } = useQuery({
    queryKey: ['incidents', mapBounds],
    queryFn: () => {
      if (!mapBounds) return [];
      
      logger.info('Fetching incidents for bounds', 'IncidentMap', { bounds: mapBounds });
      return fetchIncidents(mapBounds);
    },
    enabled: !!mapBounds,
    staleTime: 60000, // Increase staleTime to 1 minute to reduce flickering
    refetchInterval: 120000, // Increase refetchInterval to 2 minutes
    // Add retry delay to minimize UI disruption
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * (2 ** attempt), 30000),
    onSuccess: (data) => {
      if (Array.isArray(data)) {
        logger.info('Setting incidents', 'IncidentMap', { count: data.length });
        setIncidents(data);
      }
      setIsMapLoading(false);
    },
    onError: (error) => {
      logger.error(error, 'fetch_incidents');
      setIsMapLoading(false);
    }
  });

  // Filter valid incidents for display on the map
  const validIncidents = useMemo(() => {
    logger.info('Filtering valid incidents', 'IncidentMap', { total: incidents?.length || 0 });
    // only keep incidents with valid lat/lng
    return (incidents || []).filter(inc => {
      if (!inc.location?.coordinates) return false;
      const [lng, lat] = inc.location.coordinates;
      return !isNaN(lat) && !isNaN(lng);
    });
  }, [incidents]);

  // Memoize all map configuration
  const mapConfig = useMemo(() => ({
    minZoom: 10,
    maxZoom: 18,
    bounceAtZoomLimits: true,
    zoomControl: false,
  }), []);

  // Memoize tile layer config
  const tileLayerConfig = useMemo(() => ({
    attribution: '© OpenStreetMap contributors',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    crossOrigin: 'anonymous'
  }), []);

  // Default to Haiti:
  // (lat ~18.9712, lng ~-72.2852; zoom around 8 to see the country fairly close)
  const defaultCenter: [number, number] = useMemo(() => [18.533333, -72.333333], []);
  
  // Called once when Leaflet has created the map instance
  const handleMapReady = useCallback(
    (mapInstance: L.Map) => {
      setMap(mapInstance);
      
      // If userLocation (lat,lng) doesn't exist yet, default to Haiti
      if (!userLocation) {
        mapInstance.setView(defaultCenter, 16);
      }

      // Then compute initial Bounds
      const bounds = mapInstance.getBounds();
      setMapBounds({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      });
    },
    [setMap, setMapBounds, userLocation, defaultCenter]
  );

  // Modify the useEffect that watches userLocation changes
  useEffect(() => {
    if (!map) return;
    
    // Check if location is denied or we've already set the initial location
    const isLocationDenied = locationPermission === 'denied' || !!locationError;
    
    if (!mapState.hasSetInitialLocation) {
      // If user location is available, check if in Haiti
      if (userLocation) {
        const userIsInHaiti = isInHaiti(userLocation.lat, userLocation.lng);
        
        if (userIsInHaiti) {
          // If user is in Haiti, center on their location
          map.setView([userLocation.lat, userLocation.lng], 16, { animate: true });
          setMapState({
            center: [userLocation.lat, userLocation.lng],
            zoom: 16,
            hasSetInitialLocation: true
          });
        } else {
          // If user is not in Haiti, center on Haiti
          map.setView(defaultCenter, 8, { animate: true });
          setMapState({
            center: defaultCenter,
            zoom: 8,
            hasSetInitialLocation: true
          });
          setHasHandledNonHaitiLocation(true);
        }
      } 
      // If location is denied, set a default location but mark as initialized
      else if (isLocationDenied) {
        map.setView(defaultCenter, 8, { animate: true });
        setMapState({
          center: defaultCenter,
          zoom: 8,
          hasSetInitialLocation: true
        });
      }
      // Otherwise wait for location or explicit denial
    }
  }, [map, userLocation, mapState.hasSetInitialLocation, setMapState, defaultCenter, locationPermission, locationError]);
  
  // Add a new effect to handle when user location changes after initial setup
  useEffect(() => {
    if (!map || !userLocation || hasUserInteractedWithMap) return;
    
    const userIsInHaiti = isInHaiti(userLocation.lat, userLocation.lng);
    
    if (!userIsInHaiti && !hasHandledNonHaitiLocation) {
      map.setView(defaultCenter, 8, { animate: true });
      setMapState({
        center: defaultCenter,
        zoom: 8,
        hasSetInitialLocation: true
      });
      setHasHandledNonHaitiLocation(true);
    }
  }, [map, userLocation, hasUserInteractedWithMap, setMapState, hasHandledNonHaitiLocation, defaultCenter]);
  
  // This callback updates the marker-slug route
  const handleMarkerClick = useCallback(
    (slug: string) => {
      navigate(`/incident/${slug}`);
    },
    [navigate]
  );

  // Modify the recenter button handler to respect Haiti-only constraint
  const handleRecenter = useCallback(() => {
    if (!map) return;
    
    if (userLocation) {
      const userIsInHaiti = isInHaiti(userLocation.lat, userLocation.lng);
      
      if (userIsInHaiti) {
        // If user is in Haiti, center on their location
        map.setView([userLocation.lat, userLocation.lng], mapState.zoom, { animate: true });
      } else {
        // If user is not in Haiti, center on Haiti
        map.setView(defaultCenter, 8, { animate: true });
      }
    } else {
      // If no user location, center on Haiti
      map.setView(defaultCenter, 8, { animate: true });
    }
  }, [map, userLocation, mapState.zoom, defaultCenter]);

  // This is triggered by the BoundsUpdater each time the map moves
  const handlePositionChange = useCallback((center: [number, number], zoom: number) => {
    setMapState(prev => ({
      ...prev,
      center,
      zoom
    }));
  }, [setMapState]);

  // Force a slight resize after mounting
  useEffect(() => {
    if (!map) return;
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 200);
    return () => clearTimeout(timer);
  }, [map]);

  // Group incidents by location to determine which ones are close to each other
  const groupedIncidents = useMemo(() => {
    const groups: Record<string, Incident[]> = {};
    
    validIncidents.forEach(incident => {
      if (!incident.location?.coordinates) return;
      
      // Round coordinates to 4 decimal places (approx 11 meters) to group nearby incidents
      const [lng, lat] = incident.location.coordinates;
      const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(incident);
    });
    
    return groups;
  }, [validIncidents]);

  // Add effect to handle force recenter
  useEffect(() => {
    if (!map || !userLocation || !mapState.forceRecenter) return;
    
    // Force recenter the map
    map.setView([userLocation.lat, userLocation.lng], 16, { animate: true });
    
    // Clear the force recenter flag
    setMapState({
      ...mapState,
      forceRecenter: false
    });
  }, [map, userLocation, mapState.forceRecenter, setMapState, mapState]);

  // Add this effect after the other useEffect that handles initial location
  useEffect(() => {
    if (!map || !userLocation || !mapState.shouldForceCenter) return;
    
    // Force center the map on user location
    map.setView([userLocation.lat, userLocation.lng], 16, { animate: true });
    
    // Reset the flag after centering
    setMapState(prev => ({
      ...prev,
      shouldForceCenter: false
    }));
  }, [map, userLocation, mapState.shouldForceCenter, setMapState]);

  return (
    <div className="h-full w-full relative">
      <MapContainer
        center={mapState.center || defaultCenter}
        zoom={mapState.zoom || 16}
        className="h-full w-full relative"
        {...mapConfig}
      >
        <ZoomControl position="bottomleft" />
        <MapController onMapReady={handleMapReady} />
        <TileLayer
          {...tileLayerConfig}
        />
        <MapUpdater 
          center={mapState.center || defaultCenter}
          zoom={mapState.zoom || 16}
        />
        <BoundsUpdater 
          onPositionChange={handlePositionChange} 
          onBoundsChange={setMapBounds}
        />
        {/* <MapDebugOverlay 
          bounds={mapBounds || null}
          isLoading={isLoading} 
          incidentCount={validIncidents.length}
        />
        <FetchDebugOverlay
          incidents={validIncidents}
          isLoading={isLoading}
          bounds={mapBounds || null}
        /> */}
        
        {userLocation && (
          <UserLocationMarker position={userLocation} />
        )}

        {Object.entries(groupedIncidents).flatMap(([locationKey, incidents]) => 
          incidents.map((incident, index) => (
            <IncidentMarker
              key={incident.id}
              incident={incident}
              isHovered={hoveredIncidentId === incident.id}
              onClick={handleMarkerClick}
              onMouseOver={() => {
                setHoveredIncidentId(incident.id);
                setSelectedIncident(incident);
              }}
              onMouseOut={() => {
                setHoveredIncidentId(null);
                setSelectedIncident(null);
              }}
              index={index}
              totalCount={incidents.length}
            />
          ))
        )}
      </MapContainer>
      <RecenterButton onClick={handleRecenter} />
    </div>
  );
});